/**
 * AssetOverview — "¿cuánto vale todo lo que tengo, en todos los hangares?"
 *
 *  - Summary tiles (locations, total value, asset rows)
 *  - Group cards por location con tabla de items (top 20 por valor)
 *  - Filter por location_type (station / structure / other)
 *  - Search por item name
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { StatTile } from './ui'

type Item = {
  type_id: number
  name: string
  quantity: number
  unit_price: number | null
  total_value: number | null
}

type Group = {
  location_id: number
  location_name: string
  location_type: string
  total_value: number
  total_item_types: number
  item_count: number
  items: Item[]
}

export function AssetOverview() {
  const [search, setSearch] = useState('')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['inventory-all-stations', forceRefetchKey],
    queryFn: () => api.inventoryAllStations(force),
  })

  const groups: Group[] = data?.groups ?? []
  const summary = data?.summary

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups
    const q = search.trim().toLowerCase()
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => it.name.toLowerCase().includes(q)),
      }))
      .filter((g) => g.items.length > 0)
  }, [groups, search])

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="stat-label">Asset overview</div>
          <div className="text-eve-muted text-xs">
            Todos tus hangares (NPC stations + player structures), agrupados
            por ubicación. Valor estimado = Jita sell × qty.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary */}
      {summary && summary.total_locations > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatTile
            variant="muted"
            size="sm"
            label="Locations"
            value={String(summary.total_locations)}
            sub={`${summary.total_asset_rows.toLocaleString('en-US')} asset rows`}
          />
          <StatTile
            variant="muted"
            size="sm"
            tone="accent"
            label="Valor total"
            value={`${formatISKShort(summary.total_value)} ISK`}
            sub={`${summary.priced_count} priced / ${summary.unpriced_count} sin precio`}
          />
          <StatTile
            variant="muted"
            size="sm"
            label="Mejor location"
            value={groups[0]?.location_name ?? '—'}
            sub={
              groups[0]
                ? `${formatISKShort(groups[0].total_value)} ISK · ${groups[0].total_item_types} tipos`
                : '—'
            }
          />
          <StatTile
            variant="muted"
            size="sm"
            label="Promedio por location"
            value={
              summary.total_locations > 0
                ? `${formatISKShort(summary.total_value / summary.total_locations)} ISK`
                : '—'
            }
          />
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3 mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar item…"
          className="bg-eve-panel border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text placeholder:text-eve-muted focus:border-eve-accent focus:outline-none w-full md:w-64"
        />
        <span className="text-eve-muted text-xs ml-auto">
          {filteredGroups.length} / {groups.length} locations
        </span>
      </div>

      {/* Groups */}
      {isLoading ? (
        <LoadingSkeleton variant="rows" rows={6} />
      ) : summary?.total_locations === 0 ? (
        <div className="text-eve-muted py-8 text-center italic">No tenés assets visibles.</div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-eve-muted py-8 text-center italic">Sin matches.</div>
      ) : (
        <div className="space-y-3">
          {filteredGroups.map((g) => (
            <LocationCard key={g.location_id} group={g} />
          ))}
        </div>
      )}

      <p className="text-xs text-eve-muted mt-3">
        Top 20 items por location (los más valiosos). Si tenés más tipos de
        items en una location, sólo se muestran los primeros 20 acá — andá al
        juego para el detalle completo. "Sin precio" = item que no está en
        Jita market (probablemente BPC, modulos, etc.).
      </p>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function LocationCard({ group }: { group: Group }) {
  const locationTypeLabel = group.location_type === 'station'
    ? 'NPC Station'
    : group.location_type === 'structure'
      ? 'Player Structure'
      : group.location_type
  return (
    <details open className="border border-eve-border rounded-md bg-eve-bg/40">
      <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-eve-panel/50 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="font-medium">{group.location_name}</div>
          <div className="text-xs text-eve-muted font-mono">
            {locationTypeLabel} · {group.total_item_types} tipos de item
            {group.total_item_types > group.items.length && ` (mostrando top ${group.items.length})`}
          </div>
        </div>
        <div className="text-right">
          <div className="stat-label">Valor estimado</div>
          <div className="font-mono text-base font-semibold text-eve-accent">
            {formatISKShort(group.total_value)} ISK
          </div>
        </div>
      </summary>
      <div className="border-t border-eve-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-eve-muted uppercase tracking-wider bg-eve-panel/30">
              <th className="py-2 px-3 font-medium">Item</th>
              <th className="py-2 px-3 font-medium text-right">Qty</th>
              <th className="py-2 px-3 font-medium text-right">Jita sell</th>
              <th className="py-2 px-3 font-medium text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((it, i) => (
              <tr
                key={`${it.type_id}-${i}`}
                className="border-b border-eve-border/40 hover:bg-eve-panel/30"
              >
                <td className="py-2 px-3 font-medium">{it.name}</td>
                <td className="py-2 px-3 text-right font-mono">
                  {it.quantity.toLocaleString('en-US')}
                </td>
                <td className="py-2 px-3 text-right font-mono">
                  {it.unit_price !== null ? formatISK(it.unit_price, 2) : '—'}
                </td>
                <td className="py-2 px-3 text-right font-mono text-eve-accent">
                  {it.total_value !== null ? formatISKShort(it.total_value) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}
